import { createNanoEvents } from "nanoevents";
import { useCallback } from "react";
import { QueryClient, useQueryClient } from "react-query";
import { Purchase } from "types";
import { invalidateArrivalCache } from "../components/DatePicker/useArrivalTimeslots";

const invalidationEmitter = createNanoEvents();

export const useInvalidate = () => {
    const queryClient = useQueryClient();

    return useCallback(
        (collection: string, id?: ID) => {
            invalidate(queryClient, collection, id);
        },
        [queryClient],
    );
};

export const invalidate = (queryClient: QueryClient, collection: string, id?: ID) => {
    queryClient.invalidateQueries([collection, "collection"]);

    if (id) {
        queryClient.invalidateQueries([collection, id]);
    }

    invalidationEmitter.emit(collection, { queryClient, id });
};

// Invalidate audits whenever a purchase is updated
invalidationEmitter.on("purchases", ({ queryClient, id }: { queryClient: QueryClient; id: string }) => {
    if (id) {
        queryClient.invalidateQueries(["audits", "collection", { objectId: id, objectClass: "purchase" }]);
        const purchase = queryClient.getQueryData<Purchase>(["purchases", id]);

        if (purchase) {
            queryClient.invalidateQueries(["purchaseItems", "collection"]);

            for (const item of purchase.items ?? []) {
                queryClient.invalidateQueries(["purchaseItems", item.id]);
            }
        }
    }
});

// Invalidate a traveler's merge/unmerge audits (contact timeline) whenever the user is invalidated.
// Prefix match covers the seller-scoped key ["users", id, "audits", sellerId] used in ContactTimeline.
invalidationEmitter.on("users", ({ queryClient, id }: { queryClient: QueryClient; id: string }) => {
    if (id) {
        queryClient.invalidateQueries(["users", id, "audits"]);
    }
});

// Invalidate products when you invalidate experiences
invalidationEmitter.on("experiences", ({ queryClient, id }: { queryClient: QueryClient; id: string }) => {
    queryClient.invalidateQueries(["products", "collection"]);
    if (id) {
        queryClient.invalidateQueries(["products", id]);
    }
});

// Invalidate products when you invalidate gift offers
invalidationEmitter.on("giftOffers", ({ queryClient, id }: { queryClient: QueryClient; id: string }) => {
    queryClient.invalidateQueries(["products", "collection"]);
    if (id) {
        queryClient.invalidateQueries(["products", id]);
    }
});

// Invalidate products when you invalidate packages
invalidationEmitter.on("packages", ({ queryClient, id }: { queryClient: QueryClient; id: string }) => {
    queryClient.invalidateQueries(["products", "collection"]);
    if (id) {
        queryClient.invalidateQueries(["products", id]);
    }
});

// Invalidate products when you invalidate wares
invalidationEmitter.on("wares", ({ queryClient, id }: { queryClient: QueryClient; id: string }) => {
    queryClient.invalidateQueries(["products", "collection"]);
    if (id) {
        queryClient.invalidateQueries(["products", id]);
    }
});

// Invalidate experiences & gift offers when you invalidate products
invalidationEmitter.on("products", ({ queryClient, id }: { queryClient: QueryClient; id: string }) => {
    queryClient.invalidateQueries(["experiences", "collection"]);
    queryClient.invalidateQueries(["giftOffers", "collection"]);
    queryClient.invalidateQueries(["packages", "collection"]);
    queryClient.invalidateQueries(["wares", "collection"]);

    if (id) {
        queryClient.invalidateQueries(["experiences", id]);
        queryClient.invalidateQueries(["giftOffers", id]);
        queryClient.invalidateQueries(["packages", id]);
        queryClient.invalidateQueries(["wares", id]);
    }
});

// Invalidate products when you invalidate partner packages
invalidationEmitter.on("partnerPackages", ({ queryClient, id }: { queryClient: QueryClient; id: string }) => {
    queryClient.invalidateQueries(["products", "collection"]);
    if (id) {
        queryClient.invalidateQueries(["products", id]);
    }
});

invalidationEmitter.on("timeslots", () => {
    // This will wipe out the special cache for useArrivalTimeslots since we manually control it for performance
    invalidateArrivalCache();
});
